/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { includes } from 'lodash';

import log from '@broadleaf/admin-components/dist/common/utils/log';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import {
  failurePrintLabel,
  queueFetch
} from '@broadleaf/admin-components/dist/oms/hooks/useFufillmentState/state/fulfillmentActions';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { getActionStatus } from '../../../utils/RmFulfillmentUtils';
import RmFulfillmentAction from '../../components/RmFulfillmentAction';
import { getSecureAssetUrl } from '@src/utils/auth-utils';

const logger = log.getLogger(
  'oms.components.FulfillmentView.action-components.PrintShippingDocuments'
);

export interface PrintShippingDocumentsProps {
  actionDefinition: IMetadataAction;
  dispatch: Function;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
}

const PrintShippingDocuments: FC<PrintShippingDocumentsProps> = props => {
  const { actionDefinition, dispatch, metadata, state } = props;
  const { attributes = {} } = actionDefinition;
  const { allowedStatuses = [] } = attributes as Record<string, any>;

  const getShippingDocumentsEndpoint = getEndpointByType(
    actionDefinition,
    'GET_SHIPPING_DOCUMENTS'
  );
  const contextParams = useContextParams(metadata, state);
  const [printLoading, setPrintLoading] = useState<boolean>(false);

  const keysPressed = useRef({});
  const [modifierHeld, setModifierHeld] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = event => {
      keysPressed.current[event.key?.toLowerCase()] = true;
      if (keysPressed.current['shift'] && keysPressed.current['r']) {
        setModifierHeld(true);
      }
    };

    const handleKeyUp = event => {
      keysPressed.current[event.key?.toLowerCase()] = false;
      if (!keysPressed.current['shift'] || !keysPressed.current['r']) {
        setModifierHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const initiatePrint = useCallback(async () => {
    try {
      let shippingDocumentsUrl =
        state.data.attributes['shippingDocuments']?.contentUrl;
      if (!shippingDocumentsUrl?.length || modifierHeld) {
        setPrintLoading(true);

        const { data: shippingDocumentsAsset } = await request(
          { method: 'post', ...getShippingDocumentsEndpoint },
          contextParams
        );

        shippingDocumentsUrl = shippingDocumentsAsset?.contentUrl;
      }

      shippingDocumentsUrl = await getSecureAssetUrl(shippingDocumentsUrl);

      if (shippingDocumentsUrl) {
        window.open(shippingDocumentsUrl, '_blank');
      }

      dispatch(queueFetch());
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      logger.error(
        `An error occurred trying to print shipping documents for fulfillment ${contextParams.fulfillmentId}`
      );

      if (state.data.attributes['shippingDocuments']?.contentUrl) {
        dispatch(
          failurePrintLabel({
            message:
              'Shipping documents have expired and no longer exist. Please regenerate the shipping documents with Shift + R'
          })
        );
      } else {
        dispatch(
          failurePrintLabel({
            message: 'Shipping documents failed to generated'
          })
        );
      }
    } finally {
      setPrintLoading(false);
    }
  }, [
    contextParams,
    dispatch,
    getShippingDocumentsEndpoint,
    state.data.attributes
  ]);

  const { isDisabled, actionTooltip } = getActionStatus(
    'PRINT_SHIPPING_DOCUMENTS',
    state.data
  );

  if (!includes(allowedStatuses, state.data.status)) {
    return <></>;
  }

  return (
    <>
      <RmFulfillmentAction
        disabled={state.isFetching || printLoading || isDisabled}
        onClick={() => {
          initiatePrint();
        }}
        tooltip={actionTooltip}
      >
        {state.data.type === 'PICKUP'
          ? (!modifierHeld && 'Print Pickup Documents') ||
            (modifierHeld && 'Regenerate Pickup Documents')
          : (!modifierHeld && 'Print Shipping Documents') ||
            (modifierHeld && 'Regenerate Shipping Documents')}
      </RmFulfillmentAction>
    </>
  );
};

export default PrintShippingDocuments;
export { PrintShippingDocuments };
